import { useEffect, useRef, useMemo, useCallback, useState } from "react";
import Quill from "quill";
import { ImageData } from "quill-image-drop-and-paste";
import { QuillModules, QuillFormats } from "./types";
import useDialogAlert from "../../hooks/DialogAlert";

interface UseQuillEditorProps {
  value?: string;
  onChange?: (html: string, text: string, quill: Quill) => void;
  onBlur?: (html: string, text: string, quill: Quill) => void;
  onImageUpload?: (files: File[]) => Promise<string[]>;
  placeholder?: string;
  readOnly?: boolean;
  theme?: "snow" | "bubble";
  modules?: QuillModules;
  formats?: QuillFormats;
  maxLength?: number;
  isDisabled?: boolean;
}

export const useQuillEditor = ({
  value = "",
  onChange,
  onBlur,
  onImageUpload,
  placeholder = "",
  readOnly = false,
  theme = "snow",
  modules: customModules,
  formats: customFormats,
  maxLength,
  isDisabled = false,
}: UseQuillEditorProps) => {
  const { snackbar } = useDialogAlert();
  const editorRef = useRef<HTMLDivElement>(null);
  const quillRef = useRef<Quill | null>(null);
  const isInitialMount = useRef(true);
  const [modalImage, setModalImage] = useState<string | null>(null);
  const [isImageUploading, setIsImageUploading] = useState(false);

  const uploadImages = useCallback(
    async (files: File[]) => {
      if (!quillRef.current || !onImageUpload) return;

      const editor = quillRef.current;
      const range = editor.getSelection(true);

      setIsImageUploading(true);
      editor.enable(false);

      try {
        const imageUrls = await onImageUpload(files);
        editor.enable(true);
        if (range !== null) {
          editor.setSelection(range.index, range.length);
        }

        let currentCursorIndex =
          range !== null ? range.index : editor.getLength();

        imageUrls.forEach((url) => {
          editor.insertEmbed(currentCursorIndex, "image", url);
          currentCursorIndex++;
        });

        editor.setSelection(currentCursorIndex, 0);
      } catch (error) {
        const message = (error as Error).message;

        snackbar({
          message: `Erro ao realizar upload. ${message}`,
          variant: "error",
        });
      } finally {
        setIsImageUploading(false);
      }
    },
    [onImageUpload, snackbar]
  );

  const imageHandler = useCallback(() => {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.setAttribute("multiple", "true");
    input.click();

    input.onchange = async () => {
      const files = input.files;
      if (!files || !files.length) return;

      await uploadImages(Array.from(files));
      input.value = "";
    };
  }, [uploadImages]);

  const defaultModules: QuillModules = useMemo(
    () => ({
      imageDropAndPaste: onImageUpload
        ? {
            handler: async (
              dataUrl: string,
              type: string,
              imageData: ImageData
            ) => {
              const file = imageData.toFile();
              if (file) {
                await uploadImages([file]);
              }
            },
          }
        : false,
      toolbar: {
        container: [
          [{ header: [1, 2, 3, false] }],
          ["bold", "italic", "underline", "strike"],
          [{ list: "ordered" }, { list: "bullet" }],
          [{ color: [] }, { background: [] }],
          ["image"],
        ],
        handlers: {
          image: onImageUpload ? imageHandler : undefined,
        },
      },
      clipboard: {
        matchVisual: false,
      },
    }),
    [onImageUpload, imageHandler, uploadImages]
  );

  const defaultFormats: QuillFormats = useMemo(
    () => [
      "header",
      "bold",
      "italic",
      "underline",
      "strike",
      "list",
      "bullet",
      "image",
      "color",
      "background",
    ],
    []
  );

  const modules = customModules || defaultModules;
  const formats = customFormats || defaultFormats;

  useEffect(() => {
    if (!editorRef.current || quillRef.current) return;

    const quill = new Quill(editorRef.current, {
      theme,
      modules: readOnly ? { toolbar: false } : modules,
      formats,
      placeholder,
      readOnly: readOnly || isDisabled,
    });

    quillRef.current = quill;

    if (value) {
      quill.clipboard.dangerouslyPasteHTML(value);
    }

    quill.on("text-change", () => {
      const html = quill.root.innerHTML;
      const text = quill.getText();

      if (maxLength && text.length > maxLength) {
        quill.deleteText(maxLength, text.length);
        return;
      }

      onChange?.(html, text, quill);
    });

    quill.on("selection-change", () => {
      onBlur?.(quill.root.innerHTML, quill.getText(), quill);
    });

    const handleImageClick = (e: Event) => {
      const target = e.target as HTMLElement;
      if (target.tagName === "IMG") {
        setModalImage((target as HTMLImageElement).src);
      }
    };

    quill.root.addEventListener("click", handleImageClick);

    return () => {
      quill.off("text-change");
      quill.off("selection-change");
      quill.root.removeEventListener("click", handleImageClick);
    };
  }, []);

  useEffect(() => {
    if (!quillRef.current || isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const currentContent = quillRef.current.root.innerHTML;
    if (currentContent !== value) {
      const selection = quillRef.current.getSelection();
      quillRef.current.clipboard.dangerouslyPasteHTML(value);
      if (selection) {
        quillRef.current.setSelection(selection);
      }
    }
  }, [value]);

  useEffect(() => {
    if (quillRef.current) {
      quillRef.current.enable(!readOnly && !isDisabled && !isImageUploading);
    }
  }, [readOnly, isDisabled, isImageUploading]);

  const charCount = useMemo(() => {
    if (!quillRef.current) return 0;
    return quillRef.current.getText().trim().length;
  }, [value]);

  return {
    editorRef,
    modalImage,
    setModalImage,
    charCount,
    isImageUploading,
  };
};
